import { AlertState } from 'alerts/Alert'
import Member from 'iam/models/Member'
import Workspace from 'iam/models/Workspace'
import { CancellationToken } from 'infra/cancellation'
import { LegendVersionId, PublishedLegend } from 'legends/PublishedLegend'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import {
  combineLatest,
  merge,
  Observable,
  of,
  Subject,
  firstValueFrom,
} from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  share,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators'
import { CursorPaged, CursorPageQuery, Id } from 'types/definitions'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { fromStream, toStream } from 'utils/mobx-util'
import { shareLatest, withCancellation } from 'utils/rx-util'
import Job from '../Job'
import { LegendSelected } from './DraftChange'
import { DraftStateSlice, PrepareContext } from './DraftStateSlice'
import { StepFactory, StepViewModel } from './OutlineViewModel'
import { AlertPort, PublishedLegendsPort } from './ports'

const TITLE = 'How can we help you?'

/**
 * Legend change.
 */
type LegendChange = [legend: PublishedLegend | null, skipFetch: boolean]

/**
 * Legend Selector view model.
 */
export default class LegendSelectorViewModel extends DraftStateSlice {
  /**
   * The singleton step for this slice.
   */
  readonly step: StepViewModel

  /**
   * Used to emit changes so we know when to trigger a save.
   *
   * @private
   */
  private readonly changesSubject = new Subject<LegendChange>()

  /**
   * The selected Legend version.
   */
  @observable private _selectedLegendVersion: PublishedLegend | null = null

  /**
   * Search text.
   */
  @observable searchText = ''

  /**
   * Emits a value when the selected Legend version was changed by the user.
   */
  readonly changes$ = this.changesSubject.asObservable().pipe(
    // Ensure we have loaded the Legend entirely before
    // we broadcast the change.
    switchMap(([legend, skipFetch]) =>
      skipFetch
        ? of(legend)
        : withCancellation(async (ct) => {
            await this.ensureLegendIsFullyLoaded(legend, ct)
            return legend
          }),
    ),
    map((legend): LegendSelected => ({ type: 'LegendSelected', legend })),
    share(),
  )

  /**
   * Stream for the selected Legend version.
   * Starts emitting after `prepare` is done.
   *
   * TL;DR: Prevents issues around initial queries in other slices that
   * depend on the selected Legend version.
   *
   * This is so slices that depend on the selected Legend
   * won't receive a default of `null` immediately, but they still
   * want to react to setting `null` explicitly.
   *
   * For example, we want the Provider Selection slice
   * to wait with its' initial query until we have the selected
   * Legend fully loaded; there's a chance the Legend will be cleared
   * if it's inactive, in which case it will emit `null`.
   *
   * If we emitted immediately, then the `prepare` step of the
   * Provider Selection slice would see it as "oh we have no Legend, so
   * nothing to load" and the screen would render, then load in the background.
   * THAT'S NOT what we want; instead we want to prevent the render
   * until we have the ACTUAL state of the provider selection, which we can't
   * get until we know the final selected Legend.
   */
  readonly selectedLegendVersion$ = merge(
    // Emit the initial selection, but after that
    // use the changes stream since that will also ensure the legend
    // gets fetched before emitting.
    // The reason we do this is that a fully loaded legend contains
    // more info that other slices depend on, and we don't want them to
    // observe a partially loaded legend.
    toStream(() => this.selectedLegendVersion, true).pipe(take(1)),
    this.changes$.pipe(map((x) => x.legend)),
  ).pipe(
    distinctUntilChanged(),
    shareLatest(),
    // Don't start emitting until the slice is ready.
    this.whileReady(),
  )

  /**
   * Streams as Observables.
   *
   * @private
   */
  private readonly $ = {
    selectedWorkspaceMember: fromStream(null, this.selectedWorkspaceMember$),
  }

  /**
   * Constructor.
   */
  constructor(
    isActive$: Observable<boolean>,
    createStep: StepFactory,
    private selectedWorkspaceMember$: Observable<Member | null>,
    private adapter: PublishedLegendsPort & AlertPort,
    private _isDisabled: () => boolean,
  ) {
    super(isActive$)
    makeObservable(this)
    this.selectedLegendVersion$.subscribe()
    this.changes$.subscribe()
    // If we haven't selected a Legend yet, whenever
    // the scroll provider instance changes we want to immediately fetch the
    // first page of Legends. We do this because we know that the user needs to
    // select a Legend.
    combineLatest([
      toStream(() => this.scrollProvider, true),
      toStream(() => this.selectedLegendVersion, true),
    ])
      .pipe(
        filter(([, legend]) => legend === null),
        switchMap(([provider]) => provider.fetch()),
        this.whileReady(),
      )
      .subscribe()
    this.step = createStep({
      stepId: 'legend',
      isValid: () => this.selectedLegendVersion !== null,
      isShortCircuited: () => this.selectedLegendVersion === null,
      outlineItemDisplay: () => {
        if (this.selectedLegendVersion === null) {
          return { title: TITLE }
        }

        return {
          title: TITLE,
          description: null,
          value: this.selectedLegendVersion.name,
          visual: {
            type: 'Image',
            url: this.selectedLegendVersion.imageUrl,
          },
        }
      },
    })
  }

  /**
   * This is the only step container.
   * We can only select a Legend when we have a Workspace.
   */
  @computed
  get stepContainers() {
    return this.$.selectedWorkspaceMember() ? [this] : []
  }

  /**
   * Whether the Legend Editor is disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * The scroll provider for Legends.
   */
  @computed
  get scrollProvider() {
    const member = this.$.selectedWorkspaceMember()
    const workspace = member?.workspace
    return new InfiniteScrollProvider<PublishedLegend>({
      limit: 20,
      fetchItems: async (params) =>
        workspace ? this.fetchLegends(workspace, params) : CursorPaged.empty,
      startFetchTaskAsPending: true,
    })
  }

  /**
   * The currently selected legend version, or `none` if not currently valid.
   */
  @computed
  get selectedLegendVersion() {
    if (!this._selectedLegendVersion) {
      return null
    }

    if (this.legends.includes(this._selectedLegendVersion)) {
      return this._selectedLegendVersion
    }

    return null
  }

  /**
   * Available Legends.
   */
  @computed
  get legends() {
    const workspace = this.$.selectedWorkspaceMember()?.workspace
    if (!workspace) {
      return []
    }

    return this.adapter.publishedLegendsForWorkspace(workspace.id)
  }

  /**
   * We do text filtering on the client.
   */
  @computed
  get filteredLegends() {
    const lower = this.searchText.toLowerCase()
    const legends = this.legends
    return legends.filter((l) => l.name.toLowerCase().includes(lower))
  }

  /**
   * Whether we are currently fetching Legend details.
   */
  @computed
  get isFetchingLegendDetails() {
    return this.ensureLegendIsFullyLoaded.pending
  }

  /**
   * Hydrates the state from the Job.
   *
   * @param context
   * @param ct
   */
  async prepare(context: PrepareContext, ct = CancellationToken.default) {
    // Wait for the WS selector to have been prepared.
    await firstValueFrom(
      this.selectedWorkspaceMember$.pipe(takeUntil(ct.asObservable())),
      { defaultValue: null },
    )

    ct.throwIfCancelled()
    if (context.type === 'EditDraft') {
      // Load existing Legend for Job.
      const job = context.job
      await this.loadLegendFromExistingDraft(job, ct)
    } else if (context.prefilledDraft.legendId) {
      await this.preselectLegendForNewDraft(context.prefilledDraft.legendId, ct)
    }
  }

  /**
   * Selects the legend and emits a change notification.
   */
  @action.bound
  selectLegend(legend: PublishedLegend | undefined | null, skipFetch = false) {
    legend ??= null
    if (legend !== this._selectedLegendVersion) {
      this.setLegend(legend)
      this.changesSubject.next([legend, skipFetch])
    }
  }

  /**
   * Sets the search text.
   *
   * @param newText
   */
  @action.bound
  setSearchText(newText: string) {
    this.searchText = newText
  }

  /**
   * Fetches legends for the specified Workspace Member.
   */
  fetchLegends = task(async (workspace: Workspace, params: CursorPageQuery) => {
    return this.adapter.findAvailableLegendVersions({
      ...params,
      workspace_id: Id.assert(workspace.id),
    })
  })

  /**
   * Ensure that the Legend is fully loaded.
   */
  private ensureLegendIsFullyLoaded = task.resolved(
    async (legend: PublishedLegend | null, ct: CancellationToken) => {
      if (legend === null) {
        return
      }
      await ct.race(this.adapter.fetchLegendVersion(legend.versionId))
    },
  )

  /**
   * Sets the legend.
   *
   * @param legend
   * @private
   */
  @action.bound
  private setLegend(legend: PublishedLegend | null) {
    this._selectedLegendVersion = legend
  }

  /**
   * Loads the Legend for an existing draft.
   * If the Legend is now inactive, clears the selection.
   * If a new Legend Version has been deployed, it is updated to the
   * latest version.
   *
   * @param job
   * @param ct
   * @private
   */
  private async loadLegendFromExistingDraft(job: Job, ct: CancellationToken) {
    const latest = await this.adapter.fetchLatestLegendVersion(
      job.legendVersionId.id,
    )
    ct.throwIfCancelled()
    if (latest.inactive) {
      // The Legend is inactive, so clear the selection.
      this.selectLegend(null)
      this.informAboutInactiveLegend()
    } else if (
      !LegendVersionId.equals(job.legendVersionId, latest.versionId) &&
      job.isDraft
    ) {
      // Select it now, triggers a change, and inform about the upgrade.
      // Since we just fetched the latest version, we can skip fetching.
      this.selectLegend(latest, true)
      this.informAboutUpgrade()
    } else {
      this.setLegend(latest)
    }
  }

  /**
   * Preselects a Legend for a new draft.
   *
   * @param legendId
   * @param ct
   * @private
   */
  private async preselectLegendForNewDraft(
    legendId: string,
    ct: CancellationToken,
  ) {
    // New draft with preselected Legend.
    const preselected = await this.adapter.fetchLatestLegendVersion(legendId)
    ct.throwIfCancelled()
    if (!preselected.inactive) {
      this.selectLegend(
        preselected,
        // Skip fetching before emitting the change since we just did.
        true,
      )
    }
  }

  /**
   * Informs the user about an inactive Legend.
   *
   * @private
   */
  private informAboutInactiveLegend() {
    this.adapter
      .newAlert()
      .saying(
        'Attention',
        'This type of request is no longer available. Please select a new one.',
      )
      .withState(AlertState.Warning)
      .show()
      .autoDismiss(60_000)
  }

  /**
   * Informs the user about the Draft having been updated.
   * @private
   */
  private informAboutUpgrade() {
    this.adapter
      .newAlert()
      .saying(
        'Notice',
        `This draft has been updated to a newer version. You don't have to do anything else.`,
      )
      .withState(AlertState.Neutral)
      .show()
      .autoDismiss(60_000)
  }
}
